import type { MedidasCorporales } from '../../../../hooks/useAdminClientePerfil';

interface Props {
  medidas: MedidasCorporales;
  setMedidas: React.Dispatch<React.SetStateAction<MedidasCorporales>>;
}

const medidaFields = [
  { key: 'altura' as const, label: 'Altura (cm)', placeholder: '170' },
  { key: 'talla' as const, label: 'Talla habitual', placeholder: 'M / 40' },
  { key: 'contorno_pecho' as const, label: 'Pecho (cm)', placeholder: '96' },
  { key: 'contorno_cintura' as const, label: 'Cintura (cm)', placeholder: '76' },
  { key: 'contorno_cadera' as const, label: 'Cadera (cm)', placeholder: '98' },
  { key: 'contorno_brazo' as const, label: 'Brazo (cm)', placeholder: '28' },
  { key: 'largo_pierna' as const, label: 'Largo pierna (cm)', placeholder: '82' },
];

export default function MedidasSection({ medidas, setMedidas }: Props) {
  const setMedida = (key: keyof MedidasCorporales, value: string) => {
    setMedidas((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
          <i className="ri-ruler-line text-lg" />
        </div>
        <div>
          <h3 className="font-serif text-brand-black text-lg font-medium">Medidas corporales</h3>
          <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
            Datos para ajuste y recomendación de tallas
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {medidaFields.map((field) => (
          <div key={field.key}>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              {field.label}
            </label>
            <input
              type="text"
              value={medidas[field.key] ?? ''}
              onChange={(e) => setMedida(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
          Notas sobre medidas
        </label>
        <textarea
          value={medidas.notas_medidas ?? ''}
          onChange={(e) => setMedidas((prev) => ({ ...prev, notas_medidas: e.target.value }))}
          rows={3}
          placeholder="Ej: Brazo derecho ligeramente más ancho..."
          className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
        />
      </div>

      {/* Tallas por marca */}
      <div className="mt-6 pt-4 border-t border-brand-gray-200">
        <h4 className="font-sans text-brand-black text-sm font-medium mb-1">
          Tallas por marca
        </h4>
        <p className="font-sans text-brand-gray-500 text-xs mb-3">
          Registra las tallas que usa el cliente en cada marca para compras online personalizadas
        </p>
        <div className="space-y-3">
          {(Object.entries(medidas.tallas_por_marca ?? {}) as [string, string][]).map(([marca, talla], idx) => (
            <div key={`${marca}-${idx}`} className="flex items-center gap-3">
              <input
                type="text"
                value={marca}
                onChange={(e) => {
                  const oldMarca = marca;
                  const newMarca = e.target.value.trim();
                  setMedidas((prev) => {
                    const tp = { ...prev.tallas_por_marca };
                    if (oldMarca !== newMarca) {
                      if (tp[newMarca] !== undefined) return prev;
                      delete tp[oldMarca];
                      if (newMarca) tp[newMarca] = talla;
                    }
                    return { ...prev, tallas_por_marca: tp };
                  });
                }}
                placeholder="Marca (ej: Zara)"
                className="flex-1 min-w-0 border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
              <input
                type="text"
                value={talla}
                onChange={(e) => {
                  setMedidas((prev) => ({
                    ...prev,
                    tallas_por_marca: { ...prev.tallas_por_marca, [marca]: e.target.value },
                  }));
                }}
                placeholder="Talla (ej: M)"
                className="w-32 border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
              <button
                type="button"
                onClick={() => {
                  setMedidas((prev) => {
                    const tp = { ...prev.tallas_por_marca };
                    delete tp[marca];
                    return { ...prev, tallas_por_marca: tp };
                  });
                }}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-red-500"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const nextIndex = Object.keys(medidas.tallas_por_marca ?? {}).length + 1;
              const newKey = `Marca ${nextIndex}`;
              setMedidas((prev) => ({
                ...prev,
                tallas_por_marca: { ...prev.tallas_por_marca, [newKey]: '' },
              }));
            }}
            className="inline-flex items-center gap-2 font-sans text-sm text-brand-black border border-brand-gray-200 px-4 py-2 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Añadir marca
          </button>
        </div>
      </div>
    </div>
  );
}